import React from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoginScreen } from "./components/LoginScreen";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { SetupAdminScreen } from "./components/SetupAdminScreen";
import { MainLayout } from "./components/layout/MainLayout";
import { Routes, Route, Navigate } from 'react-router-dom';
import { EncomendasAuthProvider } from "./context/EncomendasContext";
import ResultadoApmbbPage from './modules/resultado-apmbb/ResultadoApmbbPage';
import InstallPrompt from './components/InstallPrompt';
import { useServicoGeral } from './hooks/useServicoGeral';

// --- MÓDULOS DE DASHBOARD ---
import { AdminDashboard } from "./modules/admin/AdminDashboard";
import { EscalanteDashboard } from "./modules/escalante/EscalanteDashboard";
import { CadeteDashboard } from "./modules/cadete/CadeteDashboard";
import NewDashboard from "./modules/dashboard/NewDashboard";
import LivroOrdemPage from './modules/livro-ordens/LivroOrdemPage';
import { SoeDashboard } from "./modules/soe/SoeDashboard";
import { CmtPelotaoDashboard } from "./modules/cmt-pelotao/CmtPelotaoDashboard";

// --- GERENCIADOR ---
import { GerenciadorDashboard } from "./modules/gerenciador/GerenciadorDashboard";
import { CadetesDashboard } from "./modules/gerenciador/CadetesDashboard"; 
import { ProfessoresDashboard } from "./modules/gerenciador/ProfessoresDashboard"; 
import { MateriasDashboard } from "./modules/gerenciador/MateriasDashboard";
import { AvaliacaoDashboard } from "./modules/gerenciador/AvaliacaoDashboard";
import { CronogramaDashboard } from "./modules/gerenciador/CronogramaDashboard";

// --- ESPECÍFICOS ---
import { ProfessorAvailability } from "./modules/professor/ProfessorAvailability";
import { PerfilTurmasDashboard } from "./modules/perfil/PerfilTurmasDashboard";
import { AvaliacaoCadete } from "./modules/cadete/AvaliacaoCadete";
import { AspirantometroPage } from './modules/aspirantometro/AspirantometroPage';
import { PreceptorEvaluationPage } from './modules/preceptor/PreceptorEvaluationPage';
import { RelatorioDashboard } from "./modules/soe/RelatorioDashboard"; 
import { Avaliacao360 } from "./modules/cmt-pelotao/Avaliacao360";

// --- EXPORT DEFAULT ---
import CalculoNotas from "./modules/notas/CalculoNotas";
import EncomendasPage from "./modules/encomendas/EncomendasPage";

// --- RESULTADO ONLINE ---
import ResultadoOnlinePage from "./modules/resultado-online/ResultadoOnlinePage";
import LiderEOConfig from "./modules/resultado-online/LiderEOConfig";

// --- CMT CIA ---
import { MapaEstagioCia } from "./modules/cmt-cia/mapaestagio"; 

// 1. O "Segurança Particular" que avalia na hora do clique
const ProtecaoPainelGeral = ({ children }) => {
    const { userProfile } = useAuth();
    const { isServicoGeral, loadingServico } = useServicoGeral();

    if (loadingServico) return <LoadingSpinner />;

    const autorizado = ['soe', 'admin', 'gerenciador'].includes(userProfile?.nivel) || isServicoGeral;

    return autorizado ? children : <Navigate to="/resultado-online" replace />;
};

// 2. Redirecionamento da tela inicial
const DashboardRouter = () => {
  const { isAdmin, isGerenciador, isSoe, userProfile, isCadete, isCadeteAcademico, isEscalante } = useAuth();
  const { isServicoGeral, loadingServico } = useServicoGeral();
  
  if (loadingServico) return <LoadingSpinner />;

  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isGerenciador) return <Navigate to="/gerenciador" replace />;
  if (isSoe) return <Navigate to="/soe" replace />;
  if (userProfile?.nivel === 'professor') return <Navigate to="/professor/disponibilidade" replace />;
  
  const isAluno = isCadete || isCadeteAcademico || isEscalante || userProfile?.nivel === 'aspirante';

  if (isAluno) {
      if (isServicoGeral) return <Navigate to="/painel-geral" replace />;
      return <Navigate to="/resultado-online" replace />;
  }
  
  return <NewDashboard />;
}

const Router = () => {
  const { userProfile, loadingAuth, isAdminCreated, isAdmin, isGerenciador, isSoe, isCmtPelotao, isCmtCia } = useAuth();

  if (loadingAuth || isAdminCreated === null) return <LoadingSpinner text="Verificando sistema..." />;
  
  if (isAdminCreated === false) return <SetupAdminScreen />;
  
  if (!userProfile) {
    return (
      <Routes>
        <Route path="/avaliacao-preceptor/:id" element={<PreceptorEvaluationPage />} /> 
        <Route path="/" element={<LoginScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const allowedPerfilTurma = ['admin', 'gerenciador', 'soe', 'cadete', 'cadete_academico', 'escalante', 'cmt_cia', 'cmt_pelotao'];
  const isLiderEO = userProfile?.isLiderEO === true;

  return (
    <EncomendasAuthProvider>
      <Routes>
        <Route element={<MainLayout />}>
          <Route index element={<DashboardRouter />} />
          
          {userProfile?.nivel === 'admin' && <Route path="/admin" element={<AdminDashboard />} />}
          {userProfile?.nivel === 'escalante' && <Route path="/escalante" element={<EscalanteDashboard />} />}
          
          {(['cadete_academico', 'escalante'].includes(userProfile?.nivel)) && <Route path="/cadete" element={<CadeteDashboard />} />}
          
          {['cadete', 'cadete_academico', 'escalante', 'admin', 'gerenciador', 'soe', 'cmt_cia', 'cmt_pelotao'].includes(userProfile?.nivel) && (
            <Route path="/livro-ordens" element={<LivroOrdemPage />} />
          )}
          
          {userProfile?.nivel === 'gerenciador' && (
            <>
              <Route path="/gerenciador" element={<GerenciadorDashboard />} />
              <Route path="/gerenciador/cadetes-cadastro" element={<CadetesDashboard />} />
              <Route path="/gerenciador/professores" element={<ProfessoresDashboard />} />
              <Route path="/gerenciador/materias" element={<MateriasDashboard />} />
              <Route path="/gerenciador/avaliacoes" element={<AvaliacaoDashboard />} />
              <Route path="/gerenciador/cronograma" element={<CronogramaDashboard />} />
            </>
          )}

          {userProfile?.nivel === 'professor' && (
             <Route path="/professor/disponibilidade" element={<ProfessorAvailability />} />
          )}

          {userProfile?.nivel === 'soe' && (
              <>
                  <Route path="/soe" element={<SoeDashboard />} />
                  <Route path="/soe/relatorios" element={<RelatorioDashboard />} />
                  <Route path="/soe/avaliacao-360" element={<Avaliacao360 />} />
                  <Route path="/gerenciador/cadetes-cadastro" element={<CadetesDashboard />} />
              </>
          )}

          {/* A NOVA PROTEÇÃO DINÂMICA (Solução do Bug) */}
          <Route path="/painel-geral" element={
              <ProtecaoPainelGeral>
                  <NewDashboard />
              </ProtecaoPainelGeral>
          } />

          {userProfile?.nivel === 'cmt_pelotao' && <Route path="/cmt-pelotao" element={<CmtPelotaoDashboard />} />}
          
          {userProfile?.nivel === 'cmt_cia' && <Route path="/cmt-cia/estagio" element={<MapaEstagioCia />} />}

          <Route path="/aspirantometro" element={<AspirantometroPage />} />

          {allowedPerfilTurma.includes(userProfile?.nivel) && (
              <Route path="/perfil-turmas" element={<PerfilTurmasDashboard />} />
          )}

          {['cadete', 'cadete_academico', 'escalante', 'aspirante', 'admin', 'gerenciador', 'soe'].includes(userProfile?.nivel) && (
             <>
                <Route path="/resultado-online" element={<ResultadoOnlinePage />} />
                <Route path="/resultado-apmbb" element={<ResultadoApmbbPage />} />
             </>
          )}

          {isLiderEO && (
             <Route path="/lider-eo-config" element={<LiderEOConfig />} />
          )}
          
          {(isAdmin || isGerenciador || isSoe || isCmtPelotao || userProfile?.nivel === 'cmt_cia' || userProfile?.nivel === 'professor') ? (
            <>
              <Route path="/notas" element={<Navigate to={isAdmin ? "/admin" : (isGerenciador ? "/gerenciador" : (isSoe ? "/soe" : (userProfile?.nivel === 'professor' ? "/professor/disponibilidade" : (userProfile?.nivel === 'cmt_cia' ? "/" : "/cmt-pelotao"))))} replace />} /> 
              <Route path="/encomendas" element={<Navigate to={isAdmin ? "/admin" : (isGerenciador ? "/gerenciador" : (isSoe ? "/soe" : (userProfile?.nivel === 'cmt_cia' ? "/" : "/cmt-pelotao")))} replace />} />
              <Route path="/cadete" element={<Navigate to={isAdmin ? "/admin" : (isGerenciador ? "/gerenciador" : (isSoe ? "/soe" : (userProfile?.nivel === 'cmt_cia' ? "/" : "/cmt-pelotao")))} replace />} />
              <Route path="/escalante" element={<Navigate to={isAdmin ? "/admin" : (isGerenciador ? "/gerenciador" : (isSoe ? "/soe" : (userProfile?.nivel === 'cmt_cia' ? "/" : "/cmt-pelotao")))} replace />} />
            </>
          ) : (
            <>
              <Route path="/notas" element={<CalculoNotas />} /> 
              <Route path="/encomendas" element={<EncomendasPage />} />
            </>
          )}

          {(['cadete', 'cadete_academico', 'escalante', 'aspirante'].includes(userProfile?.nivel)) && (
              <Route path="/avaliacao-ensino" element={<AvaliacaoCadete />} />
          )}
          
          <Route path="/avaliacao-preceptor/:id" element={<Navigate to="/" replace />} />
        </Route>
        
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </EncomendasAuthProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <InstallPrompt /> 
      <Router />
    </AuthProvider>
  );
}